import {Component, OnInit} from '@angular/core';
import {ModalDismissReasons, NgbDateParserFormatter, NgbDateStruct, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subject} from 'rxjs';

import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {CrisisAlertService} from '../../services/crisis-alert.service';
import {Router} from '@angular/router';
import {Attribute} from '../../../_models/attribute.model';
import {RefData} from '../../../_models/ref.data.model';
import {NgbDateFRParserFormatter} from './ngb-date-fr-parser-formatter';
import {Entity} from '../../../_models/entity.model';

@Component({
  selector: 'app-case-search',
  templateUrl: './case-search.component.html',
  styleUrls: ['./case-search.component.scss'],
  providers: [{provide: NgbDateParserFormatter, useClass: NgbDateFRParserFormatter}]
})
export class CaseSearchComponent implements OnInit {
  expanded = false;
  courseTests: Entity[];
  validationMessage: string;
  identifierStyle = {};
  isTestDisabled = true;
  private originalCourseTests: Entity[];
  private validationMessageTimer: any;
  private searchTerms = new Subject<string>();
  private closeResult: string;
  private searchedReviewers: Observable<Attribute[]>;
  private refData: Observable<RefData>;
  private selectedReviewerId: string;
  private selectedReviewerName: string;
  private selectedCaseStatuses: Attribute[] = [];
  private selectedSpecialtyCode: string;
  private selectedIdentifier: string;
  private selectedIdentityValue: string;
  private selectedTestCode: string;
  private selectedProgramCode: string;
  private selectedCaseId: string;
  private selectedDateFrom: NgbDateStruct;
  private selectedDateTo: NgbDateStruct;
  readonly dateFrom: NgbDateStruct;
  readonly dateTo: NgbDateStruct;

  private candidateIdentifiers: Attribute[] = [{name: 'Candidate ID', code: 'candidateId'},
    {name: 'Customer Tracking ID', code: 'customerTrackingId'},
    {name: 'Master Appointment Number', code: 'masterAppointmentNumber'},
    {name: 'Registration Number', code: 'registrationNumber'}];

  constructor(private crisisAlertService: CrisisAlertService, private modalService: NgbModal, private router: Router) {
    const past = new Date();
    past.setFullYear(past.getFullYear() - 1);
    this.dateFrom = {year: past.getFullYear(), month: (past.getMonth() + 1), day: past.getDate()};
    const now = new Date();
    this.dateTo = {year: now.getFullYear(), month: (now.getMonth() + 1), day: now.getDate()};
  }

  // Push a search term into the observable stream.
  searchName(term: string): void {
    if ('' === term.trim()) {
      this.searchTerms.next('*');
    } else {
      this.searchTerms.next(term);
    }
  }

  setSelectedReviewer(reviewer: Attribute) {
    this.selectedReviewerId = reviewer.code;
    this.selectedReviewerName = reviewer.name + ' (' + reviewer.code + ')';
    this.searchTerms.next('*'); // this clears list
  }

  searchCases() {
    let query = 'searchType=ADV';
    if (this.selectedSpecialtyCode && 'ALL' !== this.selectedSpecialtyCode) {
      query = 'specialityCode=' + this.selectedSpecialtyCode;
    }
    if (this.selectedProgramCode) {
      query = query + '&programCode=' + this.selectedProgramCode;
    }
    if (this.selectedTestCode && '' !== this.selectedTestCode) {
      query = query + '&testCode=' + this.selectedTestCode;
    }
    if (this.selectedIdentifier) {
      const key = this.candidateIdentifiers.find(item => item.name === this.selectedIdentifier).code;
      if (this.selectedIdentityValue) {
        query = query + '&' + key + '=' + this.selectedIdentityValue;
      }
    }
    if (this.selectedReviewerId) {
      query = query + '&reviewerId=' + this.selectedReviewerId;
    } else {
      this.selectedReviewerName = null;
    }
    if (this.selectedCaseStatuses) {
      let queryParts = '';
      for (const status of this.selectedCaseStatuses) {
        queryParts += '&caseStatus=' + status.code;
      }
      query += queryParts;
    }
    if (this.selectedDateFrom) {
      query = query + '&dateFrom=' + this.dateToString(this.selectedDateFrom);
    }
    if (this.selectedDateTo) {
      query = query + '&dateTo=' + this.dateToStringPlusOneDay(this.selectedDateTo);
    }
    if (query === '') {
      console.log('Query is empty hence setting it to default');
      query = this.crisisAlertService.defaultQuery;
    }
    console.log('Query >>' + query);
    this.crisisAlertService.searchCases(this.selectedSpecialtyCode, query);
    this.crisisAlertService.pushListTrigger('search');
    this.crisisAlertService.pushPageNum(1);
    // clean up starts
    if (!this.selectedIdentifier && this.selectedIdentityValue) {
      this.identifierStyle = {};
      this.selectedIdentityValue = null;
    }
    if (this.selectedIdentifier && !this.selectedIdentityValue) {
      this.selectedIdentifier = null;
    }
    // clean up ends
  }

  dateToStringPlusOneDay(date: NgbDateStruct) {
    return date.year + '-' + date.month + '-' + date.day;
  }

  dateToString(date: NgbDateStruct) {
    // yyyy-MM-dd
    return date.year + '-' + date.month + '-' + date.day;
  }

  gotoCaseDetail() {
    if (this.selectedCaseId && this.selectedCaseId !== '') {
      this.crisisAlertService.getEntity('cr_clstr_id', this.selectedCaseId)
        .subscribe(x => {
          if (x && x.valid) {
            this.modalService.dismissAll();
            this.router.navigate(['/case', this.selectedCaseId]);
          } else {
            this.timeSensitiveValidationMessage('Invalid Case ID');
          }
          return null;
        });
    }
  }

  private timeSensitiveValidationMessage(msg: string) {
    this.validationMessage = msg;
    if (this.validationMessageTimer) {
      clearTimeout(this.validationMessageTimer);
    }
    this.validationMessageTimer = setTimeout(() => {
      this.validationMessage = '';
      this.selectedCaseId = null;
    }, 3000);
  }

  expand(): void {
    this.expanded ? this.expanded = false : this.expanded = true;
    this.selectedCaseId = null;
    this.validationMessage = '';
  }

  collapse() {
    if (this.expanded) {
      this.expanded = false;
    }
  }

  ngOnInit(): void {
    // this.crisisAlertService.specialityCodeObs.subscribe(specialtyCode => this.selectedSpecialtyCode = specialtyCode);
    this.resetForm();
    this.refData = this.crisisAlertService.refDataObs;
    this.crisisAlertService.refDataObs.subscribe(data => {
      this.originalCourseTests = data.tests;
      this.courseTests = data.tests;
    });
    this.crisisAlertService.searchTriggerObs.subscribe(isTrigger => {
      if (isTrigger) {
        this.resetForm();
      }
    });
  }

  resetForm() {
    this.searchTerms = new Subject<string>();
    this.searchedReviewers = this.searchTerms.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      // ignore new term if same as previous term
      distinctUntilChanged(),
      // switch to new search observable each time the term changes
      switchMap((term: string) => this.refData
        .map(data => data.reviewers
          .filter(item => item.name.toLowerCase().indexOf(term.toLowerCase()) >= 0))),
    );
    this.identifierStyle = {};
    this.selectedSpecialtyCode = null;
    this.selectedCaseStatuses = [];
    this.selectedReviewerId = null;
    this.selectedReviewerName = null;
    this.selectedProgramCode = null;
    this.selectedTestCode = null;
    this.selectedIdentifier = null;
    this.selectedIdentityValue = null;
    this.selectedDateFrom = this.dateFrom;
    this.selectedDateTo = this.dateTo;
    this.courseTests = this.originalCourseTests;
    this.isTestDisabled = true;
  }

  open(content) {
    this.modalService.open(content, {ariaLabelledBy: 'modal-title'}).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
    });
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  onVoted($event: Attribute[]) {
    this.selectedCaseStatuses = $event;
  }

  filterCourseTest() {
    this.isTestDisabled = false;
    this.crisisAlertService.refDataObs.subscribe(data => this.courseTests = data.tests.filter(x => x.auxCode === this.selectedProgramCode));
  }

  checkIdentifier() {
    if (!this.selectedIdentifier && this.selectedIdentityValue) {
      this.identifierStyle = {
        'border': '2px solid red',
        'background-color': '#f2dede',
      };
    } else {
      this.identifierStyle = {};
    }
  }

}
